/**
 * Utilities for formatting actor names (users and agents) across the application.
 * Provides human-readable labels for system agent types and user display names.
 */
import type { AgentType } from '@/services/api/generated/taskActivityService';

/**
 * Mapping of agent type codes to human-readable display names.
 * Keep this in sync with backend agent type definitions.
 */
const AGENT_DISPLAY_NAMES: Record<string, string> = {
  ingest_transcript_task: 'Transcript Ingestion',
  provider_finder: 'Provider Finder',
  artifact_digest: 'Artifact Digest',
  treatment_researcher: 'Treatment Researcher',
  treatment_request_automation: 'Treatment Request Automation',
  bland_caller: 'Voice AI',
  slack_task_bot: 'Task Bot',
  blob_sorter: 'Document Sorter',
  front_app_integration: 'Front Integration',
  task_generator: 'Task Generator',
  coverage_extractor: 'Coverage Extractor',
  intake_converter: 'Intake Converter',
  airtable_sync: 'Airtable Sync',
  system: 'System',
  backfill_script: 'Backfill Script',
  domain_event_update_orchestrator: 'Event Orchestrator',
  loa_detector: 'LOA Detector',
  rainmaker: 'Rainmaker',
  deliverable_sync: 'Deliverable Sync',
  bpo_agent: 'BPO',
  loa_chaser: 'LOA Chaser',
  external_task_rule_engine: 'External Task Rules',
  external_task_bulk_create: 'External Task Bulk Create',
  entity_sync: 'Entity Sync',
  case_activation: 'Case Activation',
  upwork: 'Upwork',
  crashing_out: 'Crashing Out',
};

/**
 * Format an agent type to a human-readable display name.
 * Falls back to the raw agent string if not found in the mapping.
 *
 * @param agent - The agent type code from the backend
 * @returns Human-readable agent name
 */
export const formatAgentName = (agent: AgentType): string => {
  return AGENT_DISPLAY_NAMES[agent] || agent;
};

/**
 * Get the display name for the actor of an event.
 * Handles users, agents, and system actions.
 *
 * @param user - User object with name/email fields
 * @param agent - Agent type code
 * @returns Display name for the actor
 */
export const getActorDisplayName = (
  user:
    | {
        first_name?: string | null;
        last_name?: string | null;
        email?: string | null;
      }
    | null
    | undefined,
  agent: AgentType | null | undefined,
): string => {
  if (user) {
    const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
    return fullName || user.email || 'Unknown';
  }
  if (agent) {
    return formatAgentName(agent);
  }
  return 'System';
};
